import { colours, fonts, getColours } from "@helpers";
import useDeviceDetect from "@helpers/deviceDetect";
import { mixpanelTrack } from "@helpers/mixpanel.helper";
import Image from "next/image";
import { usePathname } from "next/navigation";
import React, { useState, useEffect } from "react";
import styled from "styled-components";
import CTAButton from "./CTAButton";

const AppContainer = styled.div`
  box-sizing: border-box;
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  min-width: ${(props) => props.width || "304px"};
  min-height: 177px;
  width: auto;
  height: auto;
  background-color: ${colours.PALE_PEACH};
  border-radius: 12px;
  padding: 24px 16px;
  gap: 16px;
  margin-left: auto;
  margin-right: auto;
  margin-top: ${(props) => props.marginTop || "56px"};

  & a {
    height: 56px;
    width: 170px;
    max-width: 100%;
    text-align: center;
    text-underline-offset: 4px;
  }
`;

const CallToActionContainer = styled.div`
  display: flex;
  justify-content: space-around;
  gap: 8px;
  margin-bottom: 8px;
  a {
    max-width: 100%;
    width: 108px !important;
    height: 32px !important;
  }
  img {
    width: 108px !important;
    height: 32px !important;
  }
`;

const CTADesktopContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: space-between;
  margin-top: ${(props) => props.marginTop || "56px"};
  /* margin-bottom: ${(props) => props.marginBottom || "0px"}; */

  #cta {
    margin-bottom: 32px;
  }

  @media (min-width: 1172px) {
    margin-top: ${(props) => props.marginTop || "auto"};
    flex-direction: row;
    position: ${(props) => props.position || "absolute"};
    bottom: 20%;
    justify-content: ${(props) => props.justifyContent || "center"};
    gap: 32px;
    #cta {
      margin-bottom: 0px;
    }
  }
`;

const DownloadContainer = styled.div`
  border: 1px solid black;
  border-radius: 12px;
  min-height: 67px;
  min-width: 248px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  display: ${(props) => props.noDisplayDownloadContainer && "none"};
`;

const DownloadText = styled.div`
  position: relative;
  text-align: center;
  top: -13px;
  background: ${(props) => getColours(props.bgColor) || colours.WHITE};
  padding: 0 5px;
  margin: 0 8px;
  font-size: ${fonts.CAPTION};

  @media (min-width: 1172px) {
    background: ${(props) => getColours(props.bgColor) || colours.WHITE};
    background: ${(props) => getColours(props.bgColorDesktop) || colours.WHITE};
  }
`;

export const AppAndCTAContainer = ({
	ctaButtonUrl,
	ctaButtonColour,
	name,
	ctaButtonLabel,
	googlePlayUrl,
	appStoreUrl,
	marginTop = "56px",
	marginBottom = "0px",
	position = "inherit",
	bgColor,
	bgColorDesktop,
	justifyContent = "center",
	appContainerWidth = false,
	noDisplayDownloadContainer = false,
	downloadText = "Download our app to sign up",
	downloadTextDesktop = "or download our app to sign up",
	showWebSignupLink = true,
}) => {
	const { isAndroid, isIOS, isMobile, isTablet } = useDeviceDetect();

	const [isIOSPhone, setIsIOSPhone] = useState(false);
	const [isAndroidPhone, setIsAndroidPhone] = useState(false);
	const [deviceType, setDeviceType] = useState("mobile");

	useEffect(() => {
		if (isMobile || isTablet) {
			setDeviceType("mobile");
		} else {
			setDeviceType("desktop");
		}
	}, [isMobile, isTablet]);

	const pathname = usePathname();

	useEffect(() => {
		setIsIOSPhone(isIOS);
		setIsAndroidPhone(isAndroid);
	}, []);

	const renderWebSignupLink = () => {
		if (showWebSignupLink) {
			return (
				<a
					href="https://felixmobile.com.au/buy/"
					target="_blank"
					rel="noopener noreferrer"
					onClick={() =>
						mixpanelTrack("text_link_clicked", {
							cta_position: pathname,
							cta_text: "Or sign up on web",
						})
					}
				>
					Or sign up on web
				</a>
			);
		}
		return null;
	};

	if (deviceType === "mobile") {
		if (isIOSPhone) {
			return (
				<AppContainer
					marginTop={marginTop || "40px"}
					width={appContainerWidth || "304px"}
				>
					<p>{downloadText}</p>
					<a
						href={appStoreUrl}
						target="_blank"
						rel="noreferrer"
						onClick={() => {
							if (typeof window !== "undefined") {
								window.optimizely = window.optimizely || [];
								window.optimizely.push({
									type: "event",
									eventName: "download_app_clicked",
									tags: {
										platform: "iOS",
										page: pathname,
									},
								});
							}

							mixpanelTrack("download_app_clicked", {
								cta_position: pathname,
								cta_text: "appleUrl",
							});
						}}
					>
						<Image
							alt="Get it on Appstore"
							src={"/images/AppStoreBadge.png"}
							width={162.5}
							height={48}
							data-gtm-cta-text="appleUrl"
							data-gtm-cta-position={name}
						/>
					</a>
					{renderWebSignupLink()}
				</AppContainer>
			);
		}

		if (isAndroidPhone) {
			return (
				<AppContainer
					marginTop={marginTop || "40px"}
					width={appContainerWidth || "304px"}
				>
					<p>{downloadText}</p>
					<a
						href={googlePlayUrl}
						target="_blank"
						rel="noreferrer"
						onClick={() => {
							if (typeof window !== "undefined") {
								window.optimizely = window.optimizely || [];
								window.optimizely.push({
									type: "event",
									eventName: "download_app_clicked",
									tags: {
										platform: "Android",
										page: pathname,
									},
								});
							}

							mixpanelTrack("download_app_clicked", {
								cta_position: pathname,
								cta_text: "androidUrl",
							});
						}}
					>
						<Image
							alt="Get it on Google Play"
							src={"/images/PlayStoreBadge.png"}
							width={162.5}
							height={48}
							data-gtm-cta-text="androidUrl"
							data-gtm-cta-position={name}
						/>
					</a>
					{renderWebSignupLink()}
				</AppContainer>
			);
		}
	} else {
		return (
			<CTADesktopContainer
				marginTop={marginTop || "56px"}
				marginBottom={marginBottom}
				position={position}
				justifyContent={justifyContent}
			>
				{ctaButtonLabel && (
					<CTAButton
						id="cta"
						url={ctaButtonUrl}
						ctaButtonColour={ctaButtonColour}
						gtmCtaPosition={name}
						gtmCtaText={ctaButtonLabel}
					>
						{ctaButtonLabel}
					</CTAButton>
				)}
				<DownloadContainer
					noDisplayDownloadContainer={noDisplayDownloadContainer}
				>
					<DownloadText bgColor={bgColor} bgColorDesktop={bgColorDesktop}>
						{downloadTextDesktop}
					</DownloadText>
					<CallToActionContainer>
						<div>
							<a
								href={appStoreUrl}
								target="_blank"
								rel="noreferrer"
								onClick={() => {
									if (typeof window !== "undefined") {
										window.optimizely = window.optimizely || [];
										window.optimizely.push({
											type: "event",
											eventName: "download_app_clicked",
											tags: {
												platform: "iOS",
												page: pathname,
											},
										});
									}

									mixpanelTrack("download_app_clicked", {
										cta_position: pathname,
										cta_text: "appleUrl",
									});
								}}
							>
								<Image
									alt="Get it on Appstore"
									src="/images/AppStoreBadge.png"
									width={162.5}
									height={48}
									data-gtm-cta-text="appleUrl"
									data-gtm-cta-position={name}
								/>
							</a>
						</div>
						<div>
							<a
								href={googlePlayUrl}
								target="_blank"
								rel="noreferrer"
								onClick={() => {
									if (typeof window !== "undefined") {
										window.optimizely = window.optimizely || [];
										window.optimizely.push({
											type: "event",
											eventName: "download_app_clicked",
											tags: {
												platform: "Android",
												page: pathname,
											},
										});
									}

									mixpanelTrack("download_app_clicked", {
										cta_position: pathname,
										cta_text: "androidUrl",
									});
								}}
							>
								<Image
									alt="Get it on Google Play"
									src="/images/PlayStoreBadge.png"
									width={162.5}
									height={48}
									data-gtm-cta-text="androidUrl"
									data-gtm-cta-position={name}
								/>
							</a>
						</div>
					</CallToActionContainer>
				</DownloadContainer>
			</CTADesktopContainer>
		);
	}

	return null;
};
